// frontend/src/components/UpdateAvailableToast.js
import React, { useEffect, useState } from 'react';
import { deploymentsApi } from '../utils/api';
import { computeSmoothedPercent, isDeployStalled, formatElapsedMinutes } from '../utils/deploySmoothing';
import { RefreshCwIcon, XIcon, ChevronDownIcon } from './icons/LucideIcons';

const PHASE_LABELS = {
  PENDING_BUILD: 'Preparing update…',
  BUILDING: 'Building update…',
  PENDING_DEPLOY: 'Preparing to go live…',
  DEPLOYING: 'Rolling out…',
};

// Shared positioning: corner card on desktop, full-width banner lifted above
// the mobile bottom nav on phones.
const SHELL =
  'fixed z-[60] left-3 right-3 bottom-[calc(4rem+env(safe-area-inset-bottom)+0.5rem)] ' +
  'md:left-auto md:right-4 md:bottom-4 md:w-full md:max-w-sm ' +
  'bg-white border border-gray-200 rounded-lg shadow-lg';

const RefreshIcon = () => <RefreshCwIcon size={18} />;

const CloseIcon = () => <XIcon size={18} />;

const ChevronIcon = ({ open }) => (
  <ChevronDownIcon size={16} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
);

// ----- Deploying state: smoothed progress bar -----
const DeployingView = ({ deployment, onDismiss }) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, []);

  const percent = computeSmoothedPercent({
    realPercent: deployment?.percent || 0,
    phase: deployment?.phase,
    startedAt: deployment?.started_at,
    estimatedDurationMs: deployment?.estimated_duration_ms || 0,
    now,
  });
  const stalled = isDeployStalled({
    phase: deployment?.phase,
    startedAt: deployment?.started_at,
    estimatedDurationMs: deployment?.estimated_duration_ms || 0,
    now,
  });
  const stepLabel = deployment?.step || PHASE_LABELS[deployment?.phase] || 'Updating…';
  const label = stalled ? `Taking longer than usual: ${stepLabel}` : stepLabel;

  return (
    <div className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-floral-700"><RefreshIcon /></span>
          <p className="text-sm font-semibold text-gray-900">Update in progress</p>
        </div>
        <button type="button" onClick={onDismiss} aria-label="Dismiss"
          className="text-gray-500 hover:text-gray-900 min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 flex items-center justify-center -mr-2 -mt-2 md:m-0">
          <CloseIcon />
        </button>
      </div>
      <div className="w-full bg-floral-100 rounded-full h-1.5 overflow-hidden mt-3">
        <div className={`${stalled ? 'bg-dot-yellow' : 'bg-floral-700'} h-1.5 rounded-full transition-all duration-300 ease-out`} style={{ width: `${percent}%` }} />
      </div>
      <div className="flex items-center justify-between mt-2">
        <p className="text-xs text-gray-600 truncate">{label}</p>
        <span className="text-xs tabular text-gray-500 ml-2">
          {stalled ? formatElapsedMinutes(deployment?.started_at, now) : `${percent}%`}
        </span>
      </div>
    </div>
  );
};

// ----- Ready state: patch notes accordion + Refresh -----
const ReadyView = ({ loadedCommit, justDeployed, onRefresh, onDismiss }) => {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(null);
  const [notesError, setNotesError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    deploymentsApi.getReleaseNotes()
      .then((data) => { if (!cancelled) setNotes(data); })
      .catch(() => { if (!cancelled) setNotesError(true); });
    return () => { cancelled = true; };
  }, [loadedCommit]);

  const highlights = Array.isArray(notes?.highlights) ? notes.highlights : [];

  return (
    <div className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-floral-700"><RefreshIcon /></span>
          <div>
            <p className="text-sm font-semibold text-gray-900">
              {justDeployed ? 'Update ready' : 'Update available'}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">Refresh to get the latest version.</p>
          </div>
        </div>
        <button type="button" onClick={onDismiss} aria-label="Dismiss"
          className="text-gray-500 hover:text-gray-900 min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 flex items-center justify-center -mr-2 -mt-2 md:m-0">
          <CloseIcon />
        </button>
      </div>

      {justDeployed && (
        <div className="w-full bg-floral-100 rounded-full h-1.5 overflow-hidden mt-3">
          <div className="bg-dot-ok h-1.5 rounded-full" style={{ width: '100%' }} />
        </div>
      )}

      {(highlights.length > 0 || notesError) && (
        <div className="mt-3 border-t border-gray-200 pt-2">
          <button type="button" onClick={() => setOpen((o) => !o)}
            className="flex items-center justify-between w-full text-xs font-medium text-gray-600 hover:text-gray-900 min-h-[36px]">
            <span>What's new</span>
            <ChevronIcon open={open} />
          </button>
          {open && (
            <div className="mt-2 max-h-[40vh] overflow-y-auto">
              {notesError && highlights.length === 0 ? (
                <p className="text-xs text-gray-500 italic">Couldn't load the details.</p>
              ) : (
                <ul className="space-y-1 list-disc list-inside text-xs text-gray-700">
                  {highlights.map((h, i) => <li key={i}>{h}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      <div className="flex gap-2 mt-3">
        <button type="button" onClick={onRefresh}
          className="btn-primary flex-1">
          Refresh now
        </button>
      </div>
    </div>
  );
};

/**
 * Global update toast. Driven by useVersionCheck.
 * Props: { status: 'hidden'|'deploying'|'ready', deployment, loadedCommit, justDeployed, onRefresh, onDismiss }
 */
const UpdateAvailableToast = ({ status, deployment, loadedCommit, justDeployed, onRefresh, onDismiss }) => {
  if (status === 'hidden' || !status) return null;
  return (
    <div className={SHELL}>
      {status === 'deploying'
        ? <DeployingView deployment={deployment} onDismiss={onDismiss} />
        : <ReadyView loadedCommit={loadedCommit} justDeployed={justDeployed} onRefresh={onRefresh} onDismiss={onDismiss} />}
    </div>
  );
};

export default UpdateAvailableToast;
